import { useApi } from "@/hooks";
import { Actions, AppContext } from '@Context/common/general/context';
import { userAvatarUpdatedEmitter } from '@Context/common/general/user-events';
import { GameModes, OrderByDirections, PeriodType, ScoreFieldTypes, ScoreTypes } from '@Enums/index';
import { UserRankResult } from '@Globals/types';
import { Api } from '@Shared/schemas';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';

export const useUserProfile = () => {
  const { state } = useContext(AppContext);

  const [firstName, setFirstName] = useState<string | null>();
  const [lastName, setLastName] = useState<string | null>();
  const [profileImage, setProfileImage] = useState<string | null>();
  const [loading, setLoading] = useState(true);

  const { call: getUserData } = useApi<typeof Api.Users.GetUser>({
    api: Api.Users.GetUser,
    request: {
      user_doc_id: state.user.id || '',
    },
    onSuccess: (data) => {
      if (!data) return;
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setProfileImage(data.avatar);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!state.user.id) return;
    setLoading(true);
    getUserData();
  }, [state.user.id]);

  useEffect(() => {
    const unsubscribe = userAvatarUpdatedEmitter.on((event) => {
      setProfileImage(event.avatar);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return { firstName, lastName, profileImage, loading };
};

export const useUserBybIQ = () => {
  const { state } = useContext(AppContext);

  const [playerBybIQ, setPlayerBybIQ] = useState<number>();
  const [loading, setLoading] = useState(true);

  const { call: getUserStats } = useApi<typeof Api.Stats.GetUserStats>({
    api: Api.Stats.GetUserStats,
    request: {
      user_ids: [state.user.id || ''],
      limit: 10,
      stats: [{ score_name: 'byb_iq', score_field: ScoreFieldTypes.BYB_IQ, score_type: ScoreTypes.AVERAGE }],
      game_mode: GameModes.SCENARIO,
      is_completed: true,
      order_by: { field: 'ended_at', direction: OrderByDirections.DESC },
    },
    onSuccess: (data) => {
      if (!data) return;
      if (data.length > 0) {
        const bybIq = data[0].byb_iq;
        if (bybIq !== undefined && bybIq !== null && !isNaN(bybIq)) {
          setPlayerBybIQ(Number(bybIq.toFixed(0)));
        } else {
          setPlayerBybIQ(0);
        }
      }
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!state.user.id) return;
    setLoading(true);
    getUserStats();
  }, [state.user.id]);

  return { playerBybIQ, loading };
};

// @davidrocha9: This should be converted from dispatch to the new API fetching convention.
// This change will be implemented once a leaderboard schema is created under shared/schemas.
// Note: This update should be handled in its own PR.
export const useUserRank = () => {
  const { state, dispatch } = useContext(AppContext);

  const [leaderboardUser, setLeaderboardUser] = useState<UserRankResult | null>(null);
  const [loading, setLoading] = useState(true);

  const getUserLeaderboard = useCallback(() => {
    setLoading(true);

    dispatch({
      type: Actions.StatsActions.GetLeaderboard,
      payload: {
        user_id: state.user.id,
        from_date: dayjs().startOf('year').toDate(),
        to_date: dayjs().endOf('year').toDate(),
        period_type: PeriodType.YEARLY,
        score_field: ScoreFieldTypes.TOTAL_SCORE,
        score_type: ScoreTypes.AVERAGE,
        game_mode: GameModes.SCENARIO,
        skip_user_details: true,
      },
      disableLoaders: true,
      cacheTime: [5, 'minutes'],
      onSuccess: (data: any) => {
        if (data.length > 0) {
          setLeaderboardUser(data[0]);
        } else {
          setLeaderboardUser(null);
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    });
  }, [state.user.id]);

  useEffect(() => {
    if (!state.user.id) return;
    getUserLeaderboard();
  }, [state.user.id]);

  return { rank: leaderboardUser?.rank, loading };
};

export const useUserAccountData = () => {
  const { firstName, lastName, profileImage, loading: loadingProfile } = useUserProfile();
  const { playerBybIQ, loading: loadingBybIQ } = useUserBybIQ();
  const { rank, loading: loadingRank } = useUserRank();

  return {
    firstName,
    lastName,
    profileImage,
    playerBybIQ,
    rank,
    loading: loadingProfile || loadingBybIQ || loadingRank,
  };
};
